import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FrontendRestaurantSection } from '../../../services/restaurant/restaurantSectionService';
import { RestaurantSectionType, getRestaurantSectionTypeDisplayName, getAvailableRestaurantSectionTypes } from '../../../types/RestaurantSectionType';
import { Plus } from 'lucide-react';

interface Props {
  sections: FrontendRestaurantSection[];
  onSelectExisting: (sectionId: string, sectionName: string, sectionType: RestaurantSectionType) => void;
  onCreateNew: (name: string, type: RestaurantSectionType) => void;
  isCreating: boolean;
}

const SelectSectionStep: React.FC<Props> = ({ sections, onSelectExisting, onCreateNew, isCreating }) => {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<RestaurantSectionType>(RestaurantSectionType.FOOD);

  const handleCreate = () => {
    if (!newName.trim()) return;
    onCreateNew(newName.trim(), newType);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text dark:text-text-dark">{t('wizard.selectSection.title')}</h2>
        <p className="text-muted dark:text-muted-dark mt-2">{t('wizard.selectSection.subtitle')}</p>
      </div>

      <div className="space-y-3">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => onSelectExisting(section.id, section.name, section.restaurantSectionType)}
            className="w-full rounded-xl border-2 border-zinc-200 dark:border-zinc-700 hover:border-emerald-500 dark:hover:border-emerald-500 transition-all p-4 text-left flex items-center justify-between"
          >
            <div>
              <h3 className="font-semibold text-text dark:text-text-dark">{section.name}</h3>
              <p className="text-sm text-muted dark:text-muted-dark">
                {getRestaurantSectionTypeDisplayName(section.restaurantSectionType)}
              </p>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-muted dark:text-muted-dark" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ))}

        {!showCreate ? (
          <button
            onClick={() => setShowCreate(true)}
            className="w-full rounded-xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 hover:border-emerald-500 dark:hover:border-emerald-500 transition-all p-4 text-center flex items-center justify-center gap-2 text-muted dark:text-muted-dark hover:text-emerald-600 dark:hover:text-emerald-400"
          >
            <Plus className="w-5 h-5" />
            {t('wizard.selectSection.createNew')}
          </button>
        ) : (
          <div className="rounded-xl border-2 border-emerald-500 dark:border-emerald-400 p-4 space-y-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('wizard.selectSection.namePlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
              autoFocus
            />
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as RestaurantSectionType)}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-background dark:bg-background-dark text-text dark:text-text-dark"
            >
              {getAvailableRestaurantSectionTypes().map((type) => (
                <option key={type} value={type}>
                  {getRestaurantSectionTypeDisplayName(type)}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 text-text dark:text-text-dark hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || isCreating}
                className="flex-1 px-4 py-2 rounded-lg bg-emerald-500 dark:bg-emerald-600 text-white dark:text-white hover:bg-emerald-600 dark:hover:bg-emerald-700 transition disabled:opacity-50"
              >
                {isCreating ? t('wizard.selectSection.creating') : t('common.create')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectSectionStep;
